import PropTypes from 'prop-types';
import React, { useState } from 'react';
import axios from 'axios';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Message, Avatar } from 'rhinostyle';
import { AppConstants } from '../constants';
import { DataHelpers } from '../helpers';
import PdfViewerContainer from '../containers/PdfViewerContainer';
import { toggleLightboxOpen } from '../reducers/threadReducer';
import { handleDownloadClick, DOWNLOAD_WARNING } from '../helpers/PdfViewerHelpers';
import { isAndroid } from '../helpers/BrowserHelpers';
import { useAuthenticatedImageUrl } from '../hooks';

const IMAGE_CLASS_OPTIONS = {
  loaded: 'is-loaded',
  error: 'has-error',
  default: '',
};

const Attachment = (props) => {
  const dispatch = useDispatch();
  const {
    attachment,
    incoming,
    isNote,
    toggleLightbox,
  } = props;
  const [pdfViewerContainerOpen, setPdfViewerContainerOpen] = useState(false);
  const [imageLinkClass, setImageLinkClass] = useState(IMAGE_CLASS_OPTIONS.default);
  const isNativeApp = useSelector((state) => state.nativeApp.isNativeApp);
  const isNativeAndroid = isNativeApp && isAndroid();

  const showPdfViewer = () => {
    setPdfViewerContainerOpen(true);
  };

  const handlePdfViewerClose = () => {
    setPdfViewerContainerOpen(false);
  };

  const handleImageLoad = () => {
    setImageLinkClass(IMAGE_CLASS_OPTIONS.loaded);
  };

  const handleImageError = () => {
    setImageLinkClass(IMAGE_CLASS_OPTIONS.error);
  };

  const filename = attachment.attachmentUrl;
  const isShareLinkWithThumbnail = !!attachment?.shortLink;
  const shareLinkUrl = attachment?.shortLink?.longUrl;
  const url = `${AppConstants.FILE_BASE_URL}${filename}`;
  const imageThumbnailUrl = `${url}?size=thumb`;
  const fileType = attachment.type || DataHelpers.getFileMimeType(url);
  const originalFilename = attachment.name;
  const direction = incoming ? 'inbound' : 'outbound';

  const blobUrl = useAuthenticatedImageUrl(imageThumbnailUrl);

  const getMessageType = () => {
    if (isNote) return 'note';
    else return incoming ? 'default' : 'primary';
  };

  const messageClass = cx('convo__item__body__msg__attachments__file', {
    'u-p-a-0': isNote,
  });
  const attachmentIconClass = cx('convo__item__attachment-icon', {
    'is-note': isNote,
  });
  const attachmentTextClass = cx('convo__item__body__msg__attachments__file__text', {
    'is-note': isNote,
  });

  // Is the file type not one that we have the abiilty to preview?
  // Don't render it.
  if (!AppConstants.SUPPORTED_FILE_TYPES_FOR_PREVIEW.includes(fileType)) {
    return null;
  }
  const linkClass = `convo__item__body__msg__attachments__link ${imageLinkClass}`;

  if (isShareLinkWithThumbnail) {
    return (
      <a className={linkClass} href={shareLinkUrl} target="_blank" rel="noreferrer">
        <img onLoad={handleImageLoad} onError={handleImageError} src={blobUrl} alt="" />
      </a>
    );
  }

  if (fileType === AppConstants.MIME_TYPE_APPLICATION_PDF) {
    return (
      <Message className={messageClass} type={getMessageType()} direction={direction}>
        <Button
          reset
          onClick={showPdfViewer}
        >
          <Icon icon="attachment" className={attachmentIconClass} />&nbsp;
          <span className={attachmentTextClass}>
            {originalFilename}
          </span>
        </Button>
        <PdfViewerContainer
          open={pdfViewerContainerOpen}
          attachmentUrl={url}
          fileName={originalFilename}
          handlePdfViewerClose={handlePdfViewerClose}
        />
      </Message>
    );
  }
  if (fileType === AppConstants.VCARD_FILE_TYPE) {
    const vCardClass = cx('vcard', {
      'vcard-inbound': direction === 'inbound',
      'vcard-outbound': direction === 'outbound',
    });
    return (
      <div className="u-flex u-flex-direction-row u-flex-justify-end">
        {/* Real <a> click (not a div's onClick) so the native shell treats it as a user
            download; onClick handles the web blob-save / native navigate. */}
        <a
          className={vCardClass}
          href={url}
          download={originalFilename}
          {...isNativeApp && !isNativeAndroid && { target: '_blank', rel: 'noopener noreferrer' }}
          onClick={handleVCardDownload}
        >
          {attachment?.name}
          <Avatar size="small" className="vcard-avatar" name={attachment?.name} />
          <Icon className="vcard-icon" icon="caret-right" />
        </a>
      </div>
    );
  }
  async function handleVCardDownload(e) {
    // Native: let the real click navigate to the route URL; the shell downloads it. Web: the
    // route redirects to an inline S3 URL, so blob-save to force the download.
    if (isNativeApp) {
      if (!window.confirm(DOWNLOAD_WARNING)) e.preventDefault(); // eslint-disable-line no-alert
      return;
    }
    e.preventDefault();
    if (!window.confirm(DOWNLOAD_WARNING)) return; // eslint-disable-line no-alert
    try {
      const { data } = await axios.get(url, { responseType: 'blob' });
      handleDownloadClick(URL.createObjectURL(data), filename, isNativeApp, true);
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  }

  function handleToggleLightbox() {
    if (toggleLightbox && typeof toggleLightbox === 'function') {
      toggleLightbox(true, filename);
    } else {
      dispatch(toggleLightboxOpen(filename));
    }
  }

  return (
    <Button
      reset
      onClick={handleToggleLightbox}
      className={linkClass}
    >
      <img onLoad={handleImageLoad} onError={handleImageError} src={blobUrl} alt="" />
    </Button>
  );
};

Attachment.propTypes = {
  attachment: PropTypes.object,
  incoming: PropTypes.bool,
  isNote: PropTypes.bool,
  toggleLightbox: PropTypes.func,
};

export default Attachment;
